import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { IconType } from "react-icons";
import {
  LuBriefcase,
  LuBuilding2,
  LuClock,
  LuFileText,
  LuPlay,
  LuUser,
  LuWallet,
} from "react-icons/lu";

import {
  CREATE_GROUP_LABELS,
  CreateAction,
  CreateGroup,
  createMenuItems,
  groupCreateItems,
  moveHighlight,
} from "./createMenu.utils";

const ICONS: Record<CreateAction, IconType> = {
  individual: LuUser,
  organization: LuBuilding2,
  work: LuBriefcase,
  startTimer: LuPlay,
  addTime: LuClock,
  invoice: LuFileText,
  payment: LuWallet,
};

const TILE_COLORS: Record<CreateGroup, string> = {
  clients: "bg-[#EEF2FF] text-[#4F46E5]",
  work: "bg-[#F1EEFF] text-[#6B4EFF]",
  time: "bg-[#ECFDF3] text-[#12805C]",
  billing: "bg-[#FFF4E5] text-[#B45309]",
};

type CreateMenuProps = {
  canViewBilling: boolean;
  onPick: (action: CreateAction) => void;
  onClose: () => void;
};

/** The plus button's menu (owner 2026-10-25, option A). Rendered only while open. */
export const CreateMenu: React.FC<CreateMenuProps> = ({
  canViewBilling,
  onPick,
  onClose,
}) => {
  const items = useMemo(
    () => createMenuItems(canViewBilling),
    [canViewBilling],
  );
  const groups = useMemo(() => groupCreateItems(items), [items]);
  const [highlight, setHighlight] = useState(-1);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (typing) {
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setHighlight((current) =>
          moveHighlight(
            current,
            event.key === "ArrowDown" ? 1 : -1,
            items.length,
          ),
        );
        return;
      }
      if (event.key === "Enter" && highlight >= 0) {
        event.preventDefault();
        onPick(items[highlight].action);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [items, highlight, onPick, onClose]);

  return (
    <div
      role={"menu"}
      aria-label={"Create"}
      className={
        "absolute left-[calc(100%+6px)] top-0 z-30 w-[340px] rounded-[14px] border border-[#E4E7EC] bg-white p-2 shadow-[0_12px_32px_rgba(16,24,40,0.14),0_2px_6px_rgba(16,24,40,0.06)] max-md:left-2 max-md:top-full max-md:w-[calc(100vw-32px)] max-md:max-w-[340px]"
      }>
      {groups.map((group, groupIndex) => (
        <div
          key={group.group}
          className={classNames(
            groupIndex > 0 && "mt-1 border-t border-[#EDEFF3] pt-1",
          )}>
          <div
            className={
              "px-2.5 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#9097A8]"
            }>
            {CREATE_GROUP_LABELS[group.group]}
          </div>
          {group.items.map((item) => {
            const index = items.indexOf(item);
            const Icon = ICONS[item.action];
            return (
              <button
                key={item.action}
                type={"button"}
                role={"menuitem"}
                onClick={() => onPick(item.action)}
                onMouseEnter={() => setHighlight(index)}
                className={classNames(
                  "flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2 text-left",
                  highlight === index && "bg-[#F5F3FF]",
                )}>
                <span
                  className={classNames(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px]",
                    TILE_COLORS[item.group],
                  )}>
                  <Icon className={"h-[18px] w-[18px]"} />
                </span>
                <span className={"min-w-0 flex-1"}>
                  <span
                    className={
                      "block text-[13.5px] font-bold leading-[18px] text-[#1F2433]"
                    }>
                    {item.title}
                  </span>
                  <span
                    className={
                      "block truncate text-[12px] font-medium leading-[17px] text-[#6B7185]"
                    }>
                    {item.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
